import { useEffect, useMemo, useState } from 'react';

import { getActions } from 'api/actionsClient';
import type { ActionContract } from 'model/actions';

/**
 * Shared in-memory action catalog cache.
 *
 * The action list rarely changes during a session — caching it avoids
 * one HTTP request per component that wants to know about actions
 * (palette, "+ next" dropdown, side panel, individual workflow nodes).
 *
 * Two surfaces share this cache:
 *   - `useActionsCatalog()` — React hook returning { actions, isLoading, error }
 *   - `loadActions()`       — plain async function, for non-React callers
 *                              (e.g. inside another component's `useEffect`)
 *
 * Both deduplicate concurrent fetches via the in-flight promise.
 */

let cache: ActionContract[] | null = null;
let inflight: Promise<ActionContract[]> | null = null;

/**
 * Imperatively fetch the action catalog. Idempotent — subsequent calls
 * return the cached array. Concurrent callers share a single in-flight
 * request.
 */
export const loadActions = (): Promise<ActionContract[]> => {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getActions()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
};

export interface UseActionsCatalogResult {
  actions: ActionContract[];
  isLoading: boolean;
  error: string | null;
}

/**
 * React hook surfacing the action catalog with loading + error state.
 * If the catalog is already cached, returns synchronously with
 * `isLoading: false` and no fetch is triggered.
 */
export const useActionsCatalog = (): UseActionsCatalogResult => {
  const [actions, setActions] = useState<ActionContract[]>(cache ?? []);
  const [isLoading, setIsLoading] = useState(cache === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cache) return undefined;
    let cancelled = false;
    loadActions()
      .then((data) => {
        if (cancelled) return;
        setActions(data);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load actions');
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Memo-wrap so callers that pass the whole result object to a memoised
  // child get a stable reference across re-renders without state changes.
  return useMemo(() => ({ actions, isLoading, error }), [actions, isLoading, error]);
};

/**
 * Test-only helper. Resets the module-scope cache so successive test
 * cases don't carry over fetched data. Mirrors `clearCachedToken` from
 * `api/authClient`.
 */
export const clearActionsCache = (): void => {
  cache = null;
  inflight = null;
};
